import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { DataTable, PageLayout } from "../../components/modern";
import type { Column } from "../../components/modern";
import { adminApi } from "../../utils/api";
import { showErrorToast } from "../../utils/toastNotifications";
import './UserLogs.scss';

interface Log {
  id: number;
  message: string | null;
  messageTemplate: string | null;
  level: string | null;
  timeStamp: string;
  exception: string | null;
  userId: string | null;
  requestUrl: string | null;
  queryParameters: string | null;
  requestBody: string | null;
}

export const UserLogs: React.FC = () => {
  const { t, i18n } = useTranslation(['users', 'common']);
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';

  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);

  const fetchLogs = async (page: number = 1) => {
    try {
      setLoading(true);
      const response = await adminApi.call(
        `/dashboard/log/user/${userId}?page=${page}&pageSize=${pageSize}`,
        'GET'
      );

      if (!response.success) {
        showErrorToast(response.error || t('users:messages.fetchLogsError'));
        return;
      }

      setLogs(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
      setCurrentPage(page);
    } catch (error) {
      showErrorToast(t('users:messages.fetchLogsError'));
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchLogs(currentPage);
    }
  }, [userId, currentPage, pageSize]);

  const getLevelBadge = (level: string | null) => {
    if (!level) return <span className="badge badge-secondary">-</span>;
    
    const levelClass = {
      'Information': 'badge-info',
      'Warning': 'badge-warning',
      'Error': 'badge-danger',
      'Debug': 'badge-secondary',
      'Fatal': 'badge-danger'
    }[level] || 'badge-secondary';

    return <span className={`badge ${levelClass}`}>{level}</span>;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(i18n.language === 'ar' ? 'ar-IQ' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const columns: Column[] = [
    {
      key: 'timeStamp',
      title: t('users:logs.timestamp'),
      sortable: true,
      width: '180px',
      render: (value) => formatDate(value),
    },
    {
      key: 'level',
      title: t('users:logs.level'),
      sortable: true,
      width: '120px',
      render: (value) => getLevelBadge(value),
    },
    {
      key: 'message',
      title: t('users:logs.message'),
      sortable: false,
      render: (value) => (
        <div className="log-message" title={value || '-'}>
          {value || '-'}
        </div>
      ),
    },
    {
      key: 'requestUrl',
      title: t('users:logs.requestUrl'),
      sortable: false,
      width: '250px',
      render: (value) => (
        <div className="log-url" title={value || '-'}>
          {value || '-'}
        </div>
      ),
    },
    {
      key: 'exception',
      title: t('users:logs.exception'),
      sortable: false,
      width: '100px',
      align: 'center',
      render: (value) => value ? <span className="badge badge-danger">Yes</span> : <span className="badge badge-success">No</span>,
    },
  ];

  const breadcrumbs = [
    { label: t('users:breadcrumbs.users'), path: '/users' },
    { label: t('users:logs.title') }
  ];

  return (
    <PageLayout
      title={t('users:logs.title')}
      subtitle={t('users:logs.subtitle', { userId })}
      breadcrumbs={breadcrumbs}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={logs}
        loading={loading}
        searchable={true}
        filterable={false}
        exportable={false}
        refreshable={true}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        showPageSizeSelector={false}
        onPageChange={setCurrentPage}
        onRefresh={() => fetchLogs(currentPage)}
        emptyMessage={t('users:logs.noLogs')}
      />
    </PageLayout>
  );
};

